import type { StockfishScore } from './types';

const UCI_MOVE_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

export type ParsedUciMessage =
  | { readonly type: 'uciok' }
  | { readonly type: 'readyok' }
  | {
      readonly type: 'bestmove';
      readonly move: string | null;
      readonly ponder: string | null;
    }
  | {
      readonly type: 'info';
      readonly raw: string;
      readonly depth: number | null;
      readonly multiPv: number;
      readonly nodes: number | null;
      readonly score: StockfishScore | null;
      readonly pv: readonly string[];
    }
  | {
      readonly type: 'malformed';
      readonly raw: string;
      readonly reason: string;
    }
  | { readonly type: 'other'; readonly raw: string };

/** Parses a single complete UCI output line without retaining protocol state. */
export function parseUciLine(rawLine: string): ParsedUciMessage {
  const line = rawLine.trim();
  if (line === 'uciok') {
    return { type: 'uciok' };
  }
  if (line === 'readyok') {
    return { type: 'readyok' };
  }
  if (line === 'bestmove' || line.startsWith('bestmove ')) {
    return parseBestMove(line);
  }
  if (line.startsWith('info ')) {
    return parseInfo(line);
  }

  return { type: 'other', raw: line };
}

/** Parses complete browser Worker frames and, separately, newline-only streams. */
export class UciMessageParser {
  private chunkTail = '';

  /**
   * A classic Worker `message` event is one complete frame. Newlines inside
   * that frame delimit additional complete UCI lines; its final tail is final.
   */
  public pushFrame(frame: string): readonly ParsedUciMessage[] {
    return parseCompleteLines(frame.replace(/\r\n?/g, '\n').split('\n'));
  }

  /** A true byte stream retains its tail until an actual newline arrives. */
  public pushChunk(chunk: string): readonly ParsedUciMessage[] {
    const normalized = `${this.chunkTail}${chunk}`.replace(/\r\n?/g, '\n');
    const parts = normalized.split('\n');
    this.chunkTail = parts.pop() ?? '';
    return parseCompleteLines(parts);
  }

  public reset(): void {
    this.chunkTail = '';
  }
}

function parseCompleteLines(lines: readonly string[]): readonly ParsedUciMessage[] {
  return lines
    .filter((line) => line.trim().length > 0)
    .map(parseUciLine);
}

function parseBestMove(line: string): ParsedUciMessage {
  const tokens = line.split(/\s+/);
  if (tokens[0] !== 'bestmove') {
    return {
      type: 'malformed',
      raw: line,
      reason: 'bestmove output used an invalid command token',
    };
  }

  const moveToken = tokens[1];
  const move = moveToken === '(none)' || moveToken === '0000' ? null : moveToken;

  if (move === undefined || (move !== null && !UCI_MOVE_PATTERN.test(move))) {
    return {
      type: 'malformed',
      raw: line,
      reason: 'bestmove did not contain a lowercase UCI move',
    };
  }

  if (tokens.length === 2) {
    return { type: 'bestmove', move, ponder: null };
  }

  if (move === null) {
    return {
      type: 'malformed',
      raw: line,
      reason: 'a null bestmove cannot contain a ponder suffix',
    };
  }

  const ponder = tokens[3];
  if (tokens.length !== 4 || tokens[2] !== 'ponder' || !ponder || !UCI_MOVE_PATTERN.test(ponder)) {
    return {
      type: 'malformed',
      raw: line,
      reason: 'bestmove contained an invalid ponder suffix',
    };
  }

  return { type: 'bestmove', move, ponder };
}

function parseInfo(line: string): ParsedUciMessage {
  const tokens = line.split(/\s+/);
  const depth = integerAfter(tokens, 'depth');
  const multiPv = integerAfter(tokens, 'multipv') ?? 1;
  const nodes = integerAfter(tokens, 'nodes');
  const scoreIndex = tokens.indexOf('score');
  const score = parseScore(tokens, scoreIndex);
  const pvIndex = tokens.indexOf('pv');
  const pv = pvIndex < 0
    ? []
    : tokens.slice(pvIndex + 1).filter((token) => UCI_MOVE_PATTERN.test(token));

  return {
    type: 'info',
    raw: line,
    depth,
    multiPv: Number.isSafeInteger(multiPv) && multiPv > 0 ? multiPv : 1,
    nodes,
    score,
    pv,
  };
}

function integerAfter(tokens: readonly string[], keyword: string): number | null {
  const index = tokens.indexOf(keyword);
  if (index < 0 || index + 1 >= tokens.length) {
    return null;
  }

  const value = Number(tokens[index + 1]);
  return Number.isSafeInteger(value) ? value : null;
}

function parseScore(tokens: readonly string[], scoreIndex: number): StockfishScore | null {
  if (scoreIndex < 0 || scoreIndex + 2 >= tokens.length) {
    return null;
  }

  const kind = tokens[scoreIndex + 1];
  const value = Number(tokens[scoreIndex + 2]);
  if ((kind !== 'cp' && kind !== 'mate') || !Number.isSafeInteger(value)) {
    return null;
  }

  const boundToken = tokens[scoreIndex + 3];
  const bound = boundToken === 'lowerbound'
    ? 'lower' as const
    : boundToken === 'upperbound'
      ? 'upper' as const
      : undefined;

  return bound ? { kind, value, bound } : { kind, value };
}
